import { rest } from 'msw';
import {
  appCustomersEndpoint,
  areaComparisonEndpoint,
  areaOverviewEndpoint,
  areaResultBreakdownEndpoint,
  areaResultEndpoint,
  averageNetSalePerReceiptEndpoint,
  averagePiecesPerReceipt,
  baseUrl,
  budgetPlannedEndpoint,
  budgetPlanOverviewEndpoint,
  conversionRateWidgetEndpoint,
  countEndpoint,
  currencyConversionEndpoint,
  customerBaseValueViewEndpoint,
  customerEngagementEndpoint,
  customerExperienceViewEndpoint,
  customerFeedbackEndpoint,
  departmentEndpoint,
  effectiveHoursEndpoint,
  efficiencyOverviewEndpoint,
  helpASaleEndpoint,
  salesNetWidgetEndpoint,
  kpiOverviewEndpoint,
  memberOverviewEndpoint,
  newMembersEndpoint,
  orisCustomersEndpoint,
  queueCustomerOverviewEndpoint,
  queueLengthEndpoint,
  receiptEndpoint,
  receiptSalesAnalysisEndpoint,
  salesAnalysisEndpoint,
  salesEfficiencyViewEndpoint,
  salesInsightsEndpoint,
  salesOverviewEndpoint,
  scoEndpoint,
  shareOfReceiptsEndpoint,
  sptEndpoint,
  storeOverviewEndpoint,
  summaryWidgetsEndpoint,
  takt1Endpoint,
  topPlannedEndpoint,
  topSoldEndpoint,
  transactionEndpoint,
  TransactionFilterType,
  transactionSearchEndpoint,
} from '../../constants';
import { AreaBreakdownKpi } from '../../hooks';
import { mockKpiOverviewData } from '../mocks';
import {
  getAppCustomersData,
  getAveragePiecesPerReceiptData,
  getBudgetPlannedData,
  getConversionRateData,
  getCustomerFeedbackData,
  getMockAreaBreakdownData,
  getMockAreaComparisonData,
  getMockBudgetPlanOverviewData,
  getMockConversionRateOverviewData,
  getMockCurrencyConversion,
  getMockCustomerBaseValueData,
  getMockCustomerEngagementData,
  getMockCustomerExperienceData,
  getMockEfficiencyOverviewData,
  getMockHelpASaleData,
  getMockMemberOverviewData,
  getMockORISData,
  getMockQueueCustomerOverviewData,
  getMockReceiptData,
  getMockSalesEfficiencyData,
  getMockSalesInsightsData,
  getMockSalesOverviewData,
  getMockSalesPerformanceData,
  getMockSCOShareOfReceiptsData,
  getMockShareOfReceiptsData,
  getMockStoreSalesData,
  getMockSummaryWidgetsData,
  getMockTopPlanProductsData,
  getMockTopSoldProductsData,
  getMockTransactionCountData,
  getMockTransactionData,
  getNewMembersData,
  getQueueLengthData,
  getReceiptSalesAnalysisData,
  getSalesNetPerReceiptData,
  getSalesNetWidgetData,
} from '../mocks/transaction-search.mock';

export const transactionRequest = (addDelay?: boolean) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${transactionEndpoint}`, (_req, res, ctx) => {
    return res(
      ctx.status(200),
      ctx.json(getMockTransactionData(100)),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const receiptRequest = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${receiptEndpoint}/:receiptNumber`,
    (_req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockReceiptData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const transactionCountRequest = (addDelay = false, totalCount = 200) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${countEndpoint}`, (req, res, ctx) => {
    const filterType = req.url.search.split('=')[3] as TransactionFilterType;

    return res(
      ctx.status(200),
      ctx.json(getMockTransactionCountData(filterType, totalCount)),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchSalesNetWidgetData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/v2/:storeId${salesNetWidgetEndpoint}`, (_, res, ctx) => {
    const mockData = getSalesNetWidgetData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchVisitorsBudget = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${conversionRateWidgetEndpoint}${budgetPlannedEndpoint}`,
    (_, res, ctx) => {
      const mockData = getBudgetPlannedData();

      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchNetSalesBudget = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${salesNetWidgetEndpoint}${budgetPlannedEndpoint}`,
    (_, res, ctx) => {
      const mockData = getBudgetPlannedData();

      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchConversionRateWidgetData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${conversionRateWidgetEndpoint}`, (_, res, ctx) => {
    const mockData = getConversionRateData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchShareOfReceipts = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${shareOfReceiptsEndpoint}`, (_, res, ctx) => {
    const mockData = getMockShareOfReceiptsData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchSCOShareOfReceiptsData = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${shareOfReceiptsEndpoint}${scoEndpoint}`,
    (_, res, ctx) => {
      const mockData = getMockSCOShareOfReceiptsData();

      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchNewMembers = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${newMembersEndpoint}`, (_, res, ctx) => {
    const mockData = getNewMembersData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchAppCustomersData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${appCustomersEndpoint}`, (_, res, ctx) => {
    const mockData = getAppCustomersData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchEffectiveHoursBudget = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${effectiveHoursEndpoint}${budgetPlannedEndpoint}`,
    (_, res, ctx) => {
      const mockData = getBudgetPlannedData();

      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchSalesNetPerReceiptData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${averageNetSalePerReceiptEndpoint}`, (_, res, ctx) => {
    const mockData = getSalesNetPerReceiptData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchReceiptSalesAnalysisData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${receiptSalesAnalysisEndpoint}`, (_, res, ctx) => {
    const mockData = getReceiptSalesAnalysisData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchTakt1Budget = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${takt1Endpoint}${budgetPlannedEndpoint}`,
    (_, res, ctx) => {
      const mockData = getBudgetPlannedData();

      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchSptBudget = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${sptEndpoint}${budgetPlannedEndpoint}`,
    (_, res, ctx) => {
      const mockData = getBudgetPlannedData();

      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchAveragePiecesPerReceiptData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${averagePiecesPerReceipt}`, (_, res, ctx) => {
    const mockData = getAveragePiecesPerReceiptData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchQueueLengthData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/v2/:storeId${queueLengthEndpoint}`, (_, res, ctx) => {
    const mockData = getQueueLengthData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchCustomerFeedbackData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${customerFeedbackEndpoint}`, (_, res, ctx) => {
    const mockData = getCustomerFeedbackData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchORISData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${orisCustomersEndpoint}`, (_, res, ctx) => {
    const mockData = getMockORISData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchSalesAnalysisData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${salesAnalysisEndpoint}`, (_, res, ctx) => {
    const mockData = getMockStoreSalesData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchCustomerEngagementData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${customerEngagementEndpoint}`, (_, res, ctx) => {
    const mockData = getMockCustomerEngagementData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchSalesInsightsData = (addDelay = false) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${salesInsightsEndpoint}`, (_, res, ctx) => {
    const mockData = getMockSalesInsightsData();

    return res(
      ctx.status(200),
      ctx.json(mockData),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchAreaComparisonData = (addDelay?: boolean) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${areaComparisonEndpoint}`, (_req, res, ctx) => {
    return res(
      ctx.status(200),
      ctx.json(getMockAreaComparisonData()),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchHelpASaleData = (addDelay?: boolean) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${helpASaleEndpoint}`, (_req, res, ctx) => {
    const interval = _req.url.searchParams.get('interval');
    return res(
      ctx.status(200),
      ctx.json(getMockHelpASaleData(interval || 'Daily')),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

export const fetchCurrencyConversionData = (addDelay?: boolean) =>
  rest.get(`${baseUrl}${transactionSearchEndpoint}/:storeId${currencyConversionEndpoint}`, (_req, res, ctx) => {
    return res(
      ctx.status(200),
      ctx.json(getMockCurrencyConversion()),
      ctx.set('Content-Type', 'application/json'),
      ctx.delay(addDelay ? 500 : 0),
    );
  });

// Area Overview Handlers
export const fetchSalesOverviewData = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${salesOverviewEndpoint}`,
    (_, res, ctx) => {
      const mockData = getMockSalesOverviewData();
      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );
export const fetchConversionRateOverviewData = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${conversionRateWidgetEndpoint}`,
    (_, res, ctx) => {
      const mockData = getMockConversionRateOverviewData();
      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );
export const fetchEfficiencyOverviewData = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${efficiencyOverviewEndpoint}`,
    (_, res, ctx) => {
      const mockData = getMockEfficiencyOverviewData();
      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );
export const fetchMemberOverviewData = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${memberOverviewEndpoint}`,
    (_, res, ctx) => {
      const mockData = getMockMemberOverviewData();
      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchQueueCustomerOverviewData = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${queueCustomerOverviewEndpoint}`,
    (_, res, ctx) => {
      const mockData = getMockQueueCustomerOverviewData();
      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchBudgetPlanOverviewData = (addDelay = false) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${budgetPlanOverviewEndpoint}`,
    (_, res, ctx) => {
      const mockData = getMockBudgetPlanOverviewData();
      return res(
        ctx.status(200),
        ctx.json(mockData),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchKpiOverviewData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${kpiOverviewEndpoint}`,
    (_req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(mockKpiOverviewData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchSummaryWidgetsData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${areaOverviewEndpoint}${summaryWidgetsEndpoint}`,
    (_req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockSummaryWidgetsData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchCustomerBaseValueViewData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}${areaResultEndpoint}/:storeId${customerBaseValueViewEndpoint}`,
    (_req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockCustomerBaseValueData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchSalesEfficiencyViewData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}${areaResultEndpoint}/:storeId${salesEfficiencyViewEndpoint}`,
    (_req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockSalesEfficiencyData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchCustomerExperienceViewData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}${areaResultEndpoint}/:storeId${customerExperienceViewEndpoint}`,
    (_req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockCustomerExperienceData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchAreaBreakdownData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}${areaResultEndpoint}/:storeId${areaResultBreakdownEndpoint}/:kpi`,
    (req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockAreaBreakdownData(req.params.kpi as AreaBreakdownKpi)),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchSalesPerformanceData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${storeOverviewEndpoint}${departmentEndpoint}`,
    (req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockSalesPerformanceData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchWeeklyTopSoldProductsData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${storeOverviewEndpoint}${topSoldEndpoint}`,
    (req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockTopSoldProductsData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );

export const fetchWeeklyTopPlannedProductsData = (addDelay?: boolean) =>
  rest.get(
    `${baseUrl}${transactionSearchEndpoint}/:storeId${storeOverviewEndpoint}${topPlannedEndpoint}`,
    (req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json(getMockTopPlanProductsData()),
        ctx.set('Content-Type', 'application/json'),
        ctx.delay(addDelay ? 500 : 0),
      );
    },
  );
